/*
  <selling-plans-widget> — the purchase-option picker (one-time vs. subscription plans) rendered by
  snippets/selling-plan-integration.liquid inside a product form.

  It is a custom element so that quick-add modals, which re-inject this script and clear their
  content on close, get set-up and tear-down for free: connectedCallback wires everything up and
  disconnectedCallback removes every listener and observer.
*/
if (!customElements.get('selling-plans-widget')) {
  customElements.define(
    'selling-plans-widget',
    class SellingPlansWidget extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;
        this.abortController = new AbortController();

        this.appendSellingPlanInputs();
        this.updateSellingPlanInputsValues();
        this.listenToVariantChange();
        this.listenToPurchaseOptionChange();
        this.listenToVariantFetches();
        this.listenToAddToCart();
        this.updatePrice();
        this.applyQuantityLock();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        this.variantIdObserver?.disconnect();
        this.performanceObserver?.disconnect();
        this.unsubscribeCartUpdate?.();
      }

      get listenerOptions() {
        return { signal: this.abortController.signal };
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      get shopifySection() {
        return (
          document.querySelector(`#shopify-section-${this.sectionId}`) ||
          document.querySelector(`#ProductInfo-${this.sectionId}`) // quick-add modal container
        );
      }

      /*
        The section can hold other add-to-cart forms besides its own (the installment form, and
        "Pairs well with" product cards with quick add), so target the section's product form by id.
      */
      get productForm() {
        return this.shopifySection.querySelector(`form[id="product-form-${this.sectionId}"]`);
      }

      get addToCartForms() {
        return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
      }

      /*
        The forms that buy this product: the product form and its installment form. Other cart
        forms in the section belong to other products and must not receive this product's plan.
      */
      get ownForms() {
        const forms = this.shopifySection.querySelectorAll(
          `form[id="product-form-${this.sectionId}"], form[id="product-form-installment-${this.sectionId}"]`
        );
        return forms.length > 0 ? forms : this.addToCartForms;
      }

      get variantIdInput() {
        const form = this.productForm || this.addToCartForms[0];
        return form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
      }

      get visibleSellingPlanForm() {
        return this.querySelector(`section[data-variant-id^="${this.variantIdInput.value}"]`);
      }

      get selectedPurchaseOption() {
        return this.visibleSellingPlanForm?.querySelector('input[type="radio"]:checked');
      }

      get sellingPlanInput() {
        return this.shopifySection.querySelector('.selected-selling-plan-id');
      }

      get sellingPlanInputs() {
        return this.shopifySection.querySelectorAll('.selected-selling-plan-id');
      }

      get sellingPlanInputValue() {
        return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
      }

      /*
        To add a selling plan to the cart, the form needs an input named "selling_plan" carrying the
        plan id. The snippet renders one inside the product form; other forms of this product get a copy.
      */
      appendSellingPlanInputs() {
        this.ownForms.forEach((addToCartForm) => {
          if (addToCartForm.querySelector('.selected-selling-plan-id')) return;
          addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
        });
      }

      updateSellingPlanInputsValues() {
        this.sellingPlanInputs.forEach((sellingPlanInput) => {
          sellingPlanInput.value = this.sellingPlanInputValue;
        });
      }

      /*
        Each variant has its own purchase-option box; only the selected variant's box is shown.
      */
      handleSellingPlanFormVisibility() {
        const variantId = this.variantIdInput.value;
        this.querySelectorAll('.selling_plan_theme_integration').forEach((element) => {
          element.classList.toggle('hidden', element.dataset.variantId !== variantId);
        });
      }

      handleVariantChange() {
        this.handleSellingPlanFormVisibility();
        this.updateSellingPlanInputsValues();
        this.applyQuantityLock();
      }

      listenToVariantChange() {
        this.ownForms.forEach((addToCartForm) => {
          addToCartForm.addEventListener('change', () => this.handleVariantChange(), this.listenerOptions);
        });

        if (this.variantIdInput.tagName === 'INPUT') {
          this.variantIdObserver = new MutationObserver(() => this.handleVariantChange());
          this.variantIdObserver.observe(this.variantIdInput, { attributes: true });
        }
      }

      // One delegated listener covers every variant's radios, however often the buyer switches
      listenToPurchaseOptionChange() {
        this.addEventListener(
          'change',
          (event) => {
            if (!event.target.matches('input[type="radio"]')) return;
            this.updateSellingPlanInputsValues();
            this.updatePrice();
            this.applyQuantityLock();
          },
          this.listenerOptions
        );
      }

      /*
        Selecting a variant fetches the re-rendered section, which overwrites the price and resets the
        quantity. Once that fetch completes, re-apply the selected purchase option's price and lock.
      */
      listenToVariantFetches() {
        this.performanceObserver = new PerformanceObserver((list) => {
          if (!this.isConnected) return;

          list.getEntries().forEach((entry) => {
            if (entry.initiatorType !== 'fetch') return;

            const url = new URL(entry.name);
            if (url.search.includes('variant')) {
              this.updatePrice();
              this.applyQuantityLock();
            }
          });
        });

        this.performanceObserver.observe({ entryTypes: ['resource'] });
      }

      // Reset to one-time purchase once this product has actually been added to the cart
      listenToAddToCart() {
        if (!window.subscribe || !window.PUB_SUB_EVENTS) return;

        this.unsubscribeCartUpdate = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'product-form') return;
          if (String(event.productVariantId) !== String(this.variantIdInput.value)) return;

          const oneTimeRadio = this.visibleSellingPlanForm?.querySelector(
            'input[data-radio-type="one_time_purchase"]'
          );
          if (oneTimeRadio && !oneTimeRadio.checked) {
            oneTimeRadio.checked = true;
            oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      get quantityInput() {
        return (
          this.shopifySection.querySelector(`[id="Quantity-${this.sectionId}"]`) ||
          this.shopifySection.querySelector('.quantity__input[name="quantity"]')
        );
      }

      applyQuantityLock() {
        const quantityInput = this.quantityInput;
        if (!quantityInput) return;

        // Plans priced for several packs (data-locked-quantity, set by the snippet) lock the quantity
        const lockedQuantity = Number(this.selectedPurchaseOption?.dataset.lockedQuantity) || 0;
        const quantityButtons = quantityInput
          .closest('quantity-input')
          ?.querySelectorAll('button[name="minus"], button[name="plus"]');

        if (lockedQuantity) {
          quantityInput.value = lockedQuantity;
          quantityInput.setAttribute('readonly', 'readonly');
        } else if (quantityInput.hasAttribute('readonly')) {
          // Coming off a locked plan: back to 1 so the buyer doesn't buy 3 packs at the one-time price
          quantityInput.removeAttribute('readonly');
          quantityInput.value = 1;
        }

        quantityButtons?.forEach((button) => {
          button.disabled = Boolean(lockedQuantity);
        });
      }

      get priceElement() {
        return this.shopifySection.querySelector('.price');
      }

      get regularPriceElement() {
        return this.shopifySection.querySelector('.price__regular');
      }

      get regularPriceBaseValue() {
        return this.regularPriceElement.querySelector('.price-item--regular');
      }

      get salePriceElement() {
        return this.shopifySection.querySelector('.price__sale');
      }

      get salePriceValue() {
        return this.salePriceElement.querySelector('.price-item--sale');
      }

      get regularPriceValue() {
        return this.salePriceElement.querySelector('.price-item--regular');
      }

      /*
        A compare-at price on the selected option means it is discounted: show the sale price.
        Otherwise show the regular price.
      */
      updatePrice() {
        const option = this.selectedPurchaseOption;
        if (!option) return;

        const price = option.dataset.variantPrice;
        const compareAtPrice = option.dataset.variantCompareAtPrice;

        if (!compareAtPrice || compareAtPrice === price) {
          this.regularPriceElement.style.display = 'block';
          this.regularPriceBaseValue.innerHTML = price;
          this.salePriceElement.style.display = 'none';
          this.priceElement.classList.remove('price--on-sale');
        } else {
          this.salePriceElement.style.display = 'block';
          this.regularPriceValue.innerHTML = compareAtPrice;
          this.salePriceValue.innerHTML = price;
          this.regularPriceElement.style.display = 'none';
          this.priceElement.classList.add('price--on-sale');
        }
      }
    }
  );
}
